// src/tenants/components/SystemSwitcher.jsx

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { listTenants } from '../registry';

/**
 * SystemSwitcher — dropdown for platform owners to switch between systems
 * (Platform Dashboard ↔ a tenant app).
 *
 * The list of available tenant systems is driven by src/tenants/registry.js
 * via listTenants(). Adding a tenant to the registry automatically adds it
 * to the switcher.
 *
 * `currentSystemId` identifies which system is currently active — the entry
 * matching this id renders as disabled with "Currently Active" styling.
 */
export default function SystemSwitcher({ currentSystem, currentSystemId, onSystemSwitch, onSignOut }) {
    const [isOpen, setIsOpen] = useState(false);

    const systems = [
        {
            id: 'platform',
            name: 'Platform Dashboard',
            icon: '🏢',
            current: currentSystemId === 'platform',
        },
        ...listTenants().map((t) => ({
            id: t.id,
            name: t.brand.title,
            icon: t.brand.emoji,
            current: t.id === currentSystemId,
        })),
    ];

    // Switch System button icon mirrors whichever system is currently active.
    // Falls back to a generic refresh emoji if no system is marked current
    // (e.g., currentSystemId doesn't match any registered tenant).
    const currentIcon = systems.find((s) => s.id === currentSystemId)?.icon ?? '🔄';

    const handleSystemSwitch = (systemId) => {
        console.log('🔄 Platform owner switching to system:', systemId);
        setIsOpen(false);

        if (systemId === 'platform') {
            onSystemSwitch();
        }
        // Add more system switching logic here
    };

    return (
        <div className="relative">
            {/* System Switcher Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
                <span>{currentIcon}</span>
                <span className="hidden md:block">Switch System</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Dropdown Content */}
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
                        <div className="p-3 border-b border-gray-100">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                Current System
                            </div>
                            <div className="text-sm font-medium text-gray-900">{currentSystem}</div>
                        </div>

                        <div className="p-2">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 py-1 mb-2">
                                Switch to:
                            </div>

                            {systems.map((system) => {
                                // Switching is currently only implemented for 'platform' (CHA-70 will
                                // implement cross-tenant switching). Until then, non-platform entries
                                // are disabled to avoid dead-action UX.
                                const isSwitchableTarget = system.id === 'platform' && !system.current;
                                const isDisabled = system.current || !isSwitchableTarget;

                                return (
                                    <button
                                        key={system.id}
                                        onClick={() => handleSystemSwitch(system.id)}
                                        disabled={isDisabled}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                                            system.current
                                                ? 'bg-blue-50 text-blue-700 cursor-not-allowed'
                                                : isDisabled
                                                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                                    : 'hover:bg-gray-100 text-gray-700'
                                        }`}
                                    >
                                        <span className="text-lg">{system.icon}</span>
                                        <div>
                                            <div className="text-sm font-medium">{system.name}</div>
                                            {system.current && (
                                                <div className="text-xs text-blue-600">Currently Active</div>
                                            )}
                                            {isDisabled && !system.current && (
                                                <div className="text-xs text-gray-400">Switching coming soon</div>
                                            )}
                                        </div>
                                        {!isDisabled && (
                                            <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="border-t border-gray-100 p-2">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onSignOut();
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-red-50 text-red-600 transition-colors"
                            >
                                <span>🔓</span>
                                <span className="text-sm font-medium">Sign Out</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
